(() => {
  const storageKey = 'm-wishlist-products';
  const addedClass = 'added-to-wishlist';
  const wishlistButtonSelector = '.m-wishlist-button';
  const emmaaButtonSelector = '[data-emmaa-wishlist-button]';

  const normalizeItems = (items) =>
    Array.from(
      new Set(
        (Array.isArray(items) ? items : [])
          .map((item) => (typeof item === 'string' ? item : item?.handle))
          .filter(Boolean)
      )
    );

  const readItems = () => {
    try {
      return normalizeItems(JSON.parse(localStorage.getItem(storageKey)) || []);
    } catch (error) {
      return [];
    }
  };

  const dispatchUpdate = (items) => {
    window.dispatchEvent(
      new CustomEvent('emmaa:wishlist-updated', { detail: { items: normalizeItems(items) } })
    );
  };

  class EmmaaWishlist {
    constructor() {
      this.products = readItems();
      this.onClick = this.onClick.bind(this);
      this.sync = this.sync.bind(this);

      document.addEventListener('click', this.onClick);
      window.addEventListener('storage', this.sync);
      window.addEventListener('emmaa:wishlist-updated', this.sync);

      this.sync();
      this.renderWishlistPage();
    }

    save(items, emit = true) {
      this.products = normalizeItems(items);
      localStorage.setItem(storageKey, JSON.stringify(this.products));
      this.sync(this.products);
      if (emit) dispatchUpdate(this.products);
    }

    sync(eventOrItems) {
      const eventItems = eventOrItems?.detail?.items;
      this.products = normalizeItems(
        Array.isArray(eventOrItems) ? eventOrItems : eventItems || readItems()
      );

      document.querySelectorAll(wishlistButtonSelector).forEach((button) => {
        const active = this.products.includes(button.dataset.productHandle);
        button.classList.toggle(addedClass, active);
        button.setAttribute('aria-pressed', active ? 'true' : 'false');
        button.setAttribute('aria-label', active ? 'Remove from wishlist' : 'Add to wishlist');
      });

      document.querySelectorAll(emmaaButtonSelector).forEach((button) => {
        const active = this.products.includes(button.dataset.productHandle);
        button.classList.toggle('is-active', active);
        button.setAttribute('aria-pressed', active ? 'true' : 'false');
        button.setAttribute('aria-label', active ? 'Remove from wishlist' : 'Add to wishlist');
      });

      document.querySelectorAll('.m-wishlist-count, [data-wishlist-count]').forEach((badge) => {
        badge.textContent = String(this.products.length);
        badge.classList.toggle('m:hidden', this.products.length === 0);
        badge.hidden = false;
      });

      document.body?.classList.toggle('wishlist-has-item', this.products.length > 0);
      window.EmmaaCounters?.setWishlistCount?.(this.products.length);
    }

    toggle(handle) {
      if (!handle) return;
      const items = this.products.includes(handle)
        ? this.products.filter((item) => item !== handle)
        : [...this.products, handle];
      this.save(items);
    }

    remove(handle) {
      if (!handle) return;
      this.save(this.products.filter((item) => item !== handle));
    }

    onClick(event) {
      const button = event.target.closest(wishlistButtonSelector);
      if (button) {
        event.preventDefault();
        this.toggle(button.dataset.productHandle);
        return;
      }

      const pageRemove = event.target.closest('.m-wishlist-remove-button');
      if (pageRemove) {
        event.preventDefault();
        this.remove(pageRemove.dataset.productHandle);
        pageRemove.closest('.m-wishlist-card')?.remove();
        this.toggleEmptyWishlistMessage();
        return;
      }

      if (event.target.closest('[data-wishlist-remove]')) {
        window.setTimeout(this.sync, 0);
        window.setTimeout(this.sync, 100);
      }
    }

    toggleEmptyWishlistMessage() {
      if (this.products.length) return;
      document.querySelector('.m-wishlist-page-content__wrapper')?.classList.add('m:hidden');
      document.querySelector('.m-wishlist-no-products')?.classList.remove('m:hidden');
    }

    createPageRemoveButton(handle) {
      const button = document.createElement('button');
      button.type = 'button';
      button.className = 'm-tooltip m-wishlist-remove-button m:block md:m:hidden';
      button.dataset.productHandle = handle;
      button.setAttribute('aria-label', 'Remove from wishlist');
      button.innerHTML =
        '<svg class="m-svg-icon--medium" fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden="true"><path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M6 18L18 6M6 6l12 12"></path></svg>';
      return button;
    }

    async renderWishlistPage() {
      if (window.MinimogSettings?.template !== 'page.wishlist') return;

      const container = document.querySelector('.m-wishlist-page-content__wrapper');
      if (!container || typeof formatUrl !== 'function' || typeof fetchCache !== 'function') {
        return;
      }

      const nodes = new Map();
      await Promise.all(
        this.products.map(async (handle) => {
          try {
            const html = await fetchCache(formatUrl('products', handle, 'view=grid-card-item'));
            const item = document.createElement('div');
            item.className = 'm:hidden m:column m-wishlist-card';
            item.innerHTML = html;
            if (!item.querySelector('.m-product-card')) return;
            item.appendChild(this.createPageRemoveButton(handle));
            nodes.set(handle, item);
          } catch (error) {}
        })
      );

      this.products.forEach((handle) => {
        const item = nodes.get(handle);
        if (!item) return;
        container.appendChild(item);
        item.classList.remove('m:hidden');
      });

      container.classList.add('is-visible');
      if (!nodes.size) this.toggleEmptyWishlistMessage();
      this.sync();
    }
  }

  const enhanceDrawerHearts = (root = document) => {
    root.querySelectorAll?.('[data-wishlist-remove]').forEach((button) => {
      if (button.dataset.emmaaHeartReady === 'true') return;
      button.dataset.emmaaHeartReady = 'true';
      button.setAttribute('aria-label', 'Remove from wishlist');
      button.innerHTML =
        '<svg viewBox="0 0 24 24" aria-hidden="true" focusable="false"><path d="M12 20.25C7.6 16.18 4 12.86 4 9.35C4 6.97 5.79 5.25 8.07 5.25C9.38 5.25 10.65 5.86 11.45 6.82L12 7.48L12.55 6.82C13.35 5.86 14.62 5.25 15.93 5.25C18.21 5.25 20 6.97 20 9.35C20 12.86 16.4 16.18 12 20.25Z"></path></svg>';
    });
  };

  if (!window.__emmaaWishlistReady) {
    window.__emmaaWishlistReady = true;
    window.MinimogTheme = window.MinimogTheme || {};
    window.MinimogTheme.Wishlist = new EmmaaWishlist();

    enhanceDrawerHearts();
    new MutationObserver((mutations) => {
      mutations.forEach((mutation) => {
        mutation.addedNodes.forEach((node) => {
          if (node.nodeType === 1) enhanceDrawerHearts(node);
        });
      });
    }).observe(document.documentElement, { childList: true, subtree: true });
  }
})();
